import type { HeatCell } from '../contract';
import { fmtDay, fmtKc, fmtNum, weekdayMon0 } from '../format';
import styles from '../style.module.scss';

const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
/** 5 mức đậm dần theo KC (so với ngày cao nhất của tháng); mức 0 = 0 KC. */
const LEVELS = ['#F6F0F3', '#FCE7F1', '#F3C9DE', '#EE93CA', '#C2337D', '#9A1F61'];

function levelOf(value: number, max: number): number {
  if (!(value > 0) || !(max > 0)) return 0;
  return Math.min(5, Math.max(1, Math.ceil((value / max) * 5)));
}

interface Props {
  cells: HeatCell[];
  /** Ngày có số liệu mới nhất: ô `null` SAU ngày này là "chưa tới", từ ngày này trở về trước là "chưa có file". */
  asOfDay: string | null;
  /** Có số Creator LIVE/ngày (Tổng quan) thì hiện trong chú thích ô. */
  showActive?: boolean;
  title?: string;
}

/**
 * Lịch tháng dạng lưới 7 cột (thứ Hai → Chủ nhật), ô tô theo KC; ô `null` = chưa có file ngày đó (hoặc chưa
 * tới ngày). Tự vẽ bằng div (không cần echarts). Ngữ nghĩa: danh sách ngày (role list/listitem), mỗi ô có
 * aria-label + title đầy đủ số; tiêu đề thứ và ô đệm ẩn khỏi trình đọc màn hình.
 */
const MonthHeatmap = ({ cells, asOfDay, showActive = false, title = 'Lịch theo ngày' }: Props) => {
  if (!cells.length) return null;
  const max = cells.reduce((m, c) => (c.diamonds !== null && c.diamonds > m ? c.diamonds : m), 0);
  const lead = weekdayMon0(cells[0].day);
  const blanks = Array.from({ length: lead }, (_, i) => i);
  const hasFuture = cells.some((c) => c.diamonds === null && (!asOfDay || c.day > asOfDay));
  const hasMissing = cells.some((c) => c.diamonds === null && !!asOfDay && c.day <= asOfDay);

  return (
    <section className={styles.card} aria-label={title}>
      <h2 className={styles.cardTitle}>{title}</h2>
      <div className={styles.heatGrid} role="list">
        {WEEKDAYS.map((w) => (
          <div key={w} className={styles.heatHead} aria-hidden>
            {w}
          </div>
        ))}
        {blanks.map((i) => (
          <div key={`b${i}`} className={styles.heatBlank} aria-hidden />
        ))}
        {cells.map((c) => {
          const dayNum = Number(c.day.slice(8, 10));
          if (c.diamonds === null) {
            const future = !asOfDay || c.day > asOfDay;
            const label = `${fmtDay(c.day)}: ${future ? 'chưa tới' : 'chưa có file ngày'}`;
            return (
              <div
                key={c.day}
                role="listitem"
                className={`${styles.heatCell} ${future ? styles.heatFuture : styles.heatMissing}`}
                title={label}
                aria-label={label}
              >
                <span className={styles.heatDay}>{dayNum}</span>
              </div>
            );
          }
          const lv = levelOf(c.diamonds, max);
          const label = `${fmtDay(c.day)}: ${fmtKc(c.diamonds)} KC${
            showActive && c.activeCreators !== null
              ? ` · ${fmtNum(c.activeCreators)} Creator LIVE`
              : ''
          }`;
          return (
            <div
              key={c.day}
              role="listitem"
              className={styles.heatCell}
              style={{ background: LEVELS[lv], color: lv >= 4 ? '#fff' : '#1D1D2E' }}
              title={label}
              aria-label={label}
            >
              <span className={styles.heatDay}>{dayNum}</span>
            </div>
          );
        })}
      </div>
      <div className={styles.heatLegend} aria-hidden>
        <span>Ít KC</span>
        {LEVELS.map((c) => (
          <span key={c} className={styles.heatSwatch} style={{ background: c }} />
        ))}
        <span>Nhiều KC</span>
        {(hasMissing || !hasFuture) && (
          <>
            <span className={`${styles.heatSwatch} ${styles.heatMissing}`} />
            <span>Chưa có file</span>
          </>
        )}
        {hasFuture && (
          <>
            <span className={`${styles.heatSwatch} ${styles.heatFuture}`} />
            <span>Chưa tới</span>
          </>
        )}
      </div>
    </section>
  );
};

/** Ô lịch cho MỘT Creator từ facts.series (ngày ngoài chuỗi → null). */
export function cellsFromSeries(
  month: string,
  daysInMonth: number,
  series: { days: string[]; diamonds: number[] }
): HeatCell[] {
  const byDay = new Map(series.days.map((d, i) => [d, series.diamonds[i]] as [string, number]));
  return Array.from({ length: daysInMonth }, (_, i) => {
    const day = `${month}-${String(i + 1).padStart(2, '0')}`;
    const v = byDay.get(day);
    return { day, diamonds: v === undefined ? null : v, activeCreators: null };
  });
}

export default MonthHeatmap;
